import type { ScheduledSearchResult } from '@b-stock/fe-core/bstock-next/helpers'

import { isBiddingActive } from '@/buyer-portal/helpers/auctions/buyingFormat'
import { isTopTierDeal } from '@/helpers/deals/isTopTierDeal'
import type { LowBidderDealBadgeConfig } from '@/helpers/featureFlags'
import { getBidderCount } from '@/helpers/listing/bidderCount'
import type {
  BidderCount,
  BidderCountInput,
} from '@/helpers/listing/bidderCount'

/**
 * Eligibility inputs: a search result, passed as is. Its schedule is required
 * to tell an auction phase from a Buy Now phase; any other missing field
 * fails its gate, except `storefrontId`: a listing without one is not excluded.
 */
type LowBidderDealEligibilityInput = BidderCountInput &
  ScheduledSearchResult & {
    deal?: string | null
    categories?: string[]
    storefrontId?: string
  }

/**
 * The count pair is `getBidderCount`'s on every outcome, category miss
 * included: it is a listing fact, so it must agree with what the analytics
 * emitters report for the same listing.
 */
export type LowBidderDealEligibility = BidderCount & {
  eligible: boolean
  /** The priority category whose threshold applied; `null` when none matched. */
  matchedCategory: string | null
}

/**
 * GLOB-5164 — the low-bidder gates in one place, so callers do not
 * re-derive them: priority category, bidder count strictly below that
 * category's threshold, a top-tier deal, a storefront not on the exclusion
 * list, and bidding open — zero bidders say nothing about competition before
 * bidding opens, after it ends, or on a listing with no bidding.
 *
 * Eligibility only: whether a badge renders also depends on `enabled`, which
 * the config parameter leaves out so eligibility stays computable for
 * listings that show no badge, such as the A/B control arm.
 */
export const getLowBidderDealEligibility = (
  listing: LowBidderDealEligibilityInput,
  {
    bidderThresholdsByCategory,
    excludedStorefrontIds,
  }: Pick<
    LowBidderDealBadgeConfig,
    'bidderThresholdsByCategory' | 'excludedStorefrontIds'
  >
): LowBidderDealEligibility => {
  const { bidderCount, bidderCountSource } = getBidderCount(listing)

  // A listing in several priority categories takes the lowest threshold, ties
  // broken alphabetically in a fixed locale (not the viewer's) so
  // `matchedCategory` is deterministic: the search API promises no
  // `categories` order.
  const [matched] = (listing.categories ?? [])
    .flatMap((category) => {
      // A string threshold would coerce through the `<` gate below and badge
      // the listing.
      const threshold = bidderThresholdsByCategory?.[category]
      return typeof threshold === 'number' && Number.isFinite(threshold)
        ? [{ category, threshold }]
        : []
    })
    .sort(
      (a, b) =>
        a.threshold - b.threshold || a.category.localeCompare(b.category, 'en')
    )

  if (!matched) {
    return {
      eligible: false,
      bidderCount,
      bidderCountSource,
      matchedCategory: null,
    }
  }

  return {
    // An unknown count must not pass the gate — `null < 5` is `true`.
    eligible:
      bidderCount !== null &&
      bidderCount < matched.threshold &&
      isTopTierDeal(listing.deal) &&
      isBiddingActive(listing) &&
      // Fail closed: an absent list makes nothing eligible (see
      // LowBidderDealBadgeConfig).
      Array.isArray(excludedStorefrontIds) &&
      !(
        listing.storefrontId &&
        excludedStorefrontIds.includes(listing.storefrontId)
      ),
    bidderCount,
    bidderCountSource,
    // Reported even when the count is unknown: the category gate still passed.
    matchedCategory: matched.category,
  }
}
